import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, Field, Input, Logo, PetalField } from '../design';
import { api, ApiError } from '../lib/api';
import type { InviteValidation } from '../lib/types';
import './LoginPage.css';

type Phase = 'validating' | 'invalid' | 'form' | 'submitting';
type Reason = 'invalid' | 'expired' | 'used';

const REASON_COPY: Record<Reason, string> = {
  expired: 'This invitation has expired. Ask your practitioner to send you a new one.',
  used: 'This invitation has already been used. Sign in with the password you chose.',
  invalid: "We couldn't find that invitation. Check that you copied the whole link.",
};

/**
 * Redeems an emailed invitation.
 *
 * The token arrives in the URL *fragment* (`/invite#<token>`), which the browser
 * never sends to the server — so it stays out of Apache's access log and out of
 * any Referer header. It is read once on mount and then stripped from the address
 * bar so it doesn't linger in history or over someone's shoulder.
 */
export function InvitePage() {
  const { redeemInvite } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('validating');
  const [reason, setReason] = useState<Reason>('invalid');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = window.location.hash.slice(1);
    if (!raw) {
      setPhase('invalid');
      return;
    }
    setToken(raw);
    window.history.replaceState(null, '', '/invite');

    void (async () => {
      try {
        const res = await api.post<InviteValidation>('/invites/validate', { token: raw });
        if (!res.valid) {
          setReason(res.reason);
          setPhase('invalid');
          return;
        }
        setEmail(res.email);
        setName(res.name);
        setPhase('form');
      } catch {
        setReason('invalid');
        setPhase('invalid');
      }
    })();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Those passwords don’t match.');
      return;
    }

    setPhase('submitting');
    try {
      await redeemInvite(token, password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setReason('invalid');
        setPhase('invalid');
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      setPhase('form');
    }
  }

  return (
    <div
      className="zen-login"
      style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'var(--surface-page)' }}
    >
      <div
        className="zen-login__brand"
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--navy-600)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 'var(--space-9)',
        }}
      >
        <PetalField size={520} blur={3} style={{ position: 'absolute', right: -140, top: -120, opacity: 0.5 }} />
        <Logo theme="white" height={36} />
        <div style={{ position: 'relative' }}>
          <div style={{ font: 'var(--fw-regular) 44px/1.2 var(--font-display)', color: 'var(--white)', maxWidth: '16ch' }}>
            Change happens below the surface.
          </div>
          <p style={{ font: 'var(--type-body)', color: 'var(--navy-100)', maxWidth: '38ch', marginTop: 'var(--space-6)' }}>
            Your sessions and your programs, in one place.
          </p>
        </div>
        <span style={{ position: 'relative', font: 'var(--type-caption)', color: 'var(--navy-200)' }}>zenmindware.com</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Card variant="raised" pad="lg" style={{ width: 400, maxWidth: '100%' }}>
          {phase === 'validating' && (
            <div style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>Checking your invitation…</div>
          )}

          {phase === 'invalid' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              <div>
                <h2 style={{ font: 'var(--type-h3)' }}>This link isn&rsquo;t usable</h2>
                <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                  {REASON_COPY[reason]}
                </p>
              </div>
              <Button block onClick={() => navigate('/login', { replace: true })}>
                Go to sign in
              </Button>
            </div>
          )}

          {(phase === 'form' || phase === 'submitting') && (
            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }} noValidate>
              <div>
                <h2 style={{ font: 'var(--type-h3)' }}>Welcome, {name}</h2>
                <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                  Choose a password to finish setting up your account.
                </p>
              </div>
              {/* Read-only, but present and named: without a username field the
                  browser's password manager saves the credential unlabelled. */}
              <Field label="Email">
                <Input type="email" value={email} autoComplete="username" readOnly disabled />
              </Field>
              <Field label="New password" hint="At least 8 characters.">
                <Input
                  type="password"
                  icon="lock"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </Field>
              <Field label="Confirm password" error={error ?? undefined}>
                <Input
                  type="password"
                  icon="lock"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                />
              </Field>
              <Button block type="submit" disabled={phase === 'submitting'}>
                {phase === 'submitting' ? 'Setting password…' : 'Set password and sign in'}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
